import React from 'react';
import { Helmet } from 'react-helmet';
import H1 from 'components/H1';
import H2 from 'components/H2';

import helpers from 'utils/helpers';
import { FiDollarSign, FiUsers, FiGrid, FiUser } from 'react-icons/fi';
import moment from 'moment-timezone';

import AppPageWrapper from 'components/AppPageWrapper';
import AppPageWrapperSectionHeader from 'components/AppPageWrapper/AppPageWrapperSectionHeader';
import AppPageWrapperSectionBody from 'components/AppPageWrapper/AppPageWrapperSectionBody';
import SettingsPageSubNav from 'containers/SettingsPage/SettingsPageSubNav';
import { BalanceCard } from 'components/BalanceCard';
import { TableV2 } from 'components/Table';
import { SMS_SUBSCRIPTION_UNLIMITED, ANNUAL_EMAIL_USAGE } from 'utils/constants';

import LoadingIndicator from 'components/LoadingIndicator';
import LoadingWrapper from 'components/LoadingIndicator/LoadingWrapper';
import { useAccountUsageQuery } from 'api/useAccountUsageQuery';

/**
 * Check if the account is on annual email usage.
 *
 * @param {Object} account
 * @returns {boolean}
 */
export const useIsAnnualEmailUsage = account => account?.subscriptions?.[0]?.is_annual === ANNUAL_EMAIL_USAGE;

function SettingsUsagePage(props) {
    const account = props.auth.account;
    // check if account is on annual email usage. If so, hide the cost column.
    const isEmailUsageAnnual = useIsAnnualEmailUsage(account);
    const { data: accountUsage, isLoading } = useAccountUsageQuery();

    const emailUsageByMonth = accountUsage ? accountUsage.email_usage : [];
    const smsUsageByMonth = accountUsage ? accountUsage.sms_usage : [];
    const tremendousBalance = accountUsage ? accountUsage.tremendous_balance : null;
    const firstPartyBalance = accountUsage ? accountUsage.first_party_balance : null;
    const totalUsers = accountUsage ? accountUsage.total_users : null;
    const paidUsers = accountUsage ? accountUsage.paid_users : null;
    const totalPanels = accountUsage ? accountUsage.total_panels : null;
    const totalPanelists = accountUsage ? accountUsage.total_panelists : null;

    const subscription = account?.subscriptions?.[0];
    const allowedUsers = subscription?.allowed_users || 0;
    const maxPanels = subscription?.max_panels || 0;
    const maxPanelists = subscription?.max_panelists || 0;
    const accountSmsUsageUnlimited = subscription?.sms_sub_type === SMS_SUBSCRIPTION_UNLIMITED;

    const usageCellProps = () => ({
        className: 'color-text-primary'
    });

    const emailColumns = [
        {
            key: 'month',
            title: 'Month',
            width: isEmailUsageAnnual ? '25%' : '20%',
            onCell: usageCellProps,
            render: record => moment(record.start_date).format('MMM YYYY')
        },
        {
            key: 'base-emails',
            title: 'Base Emails',
            width: isEmailUsageAnnual ? '25%' : '20%',
            onCell: usageCellProps,
            render: record => helpers.numberWithCommas(record.monthly_emails_count)
        },
        {
            key: 'used',
            title: 'Used',
            width: isEmailUsageAnnual ? '25%' : '20%',
            onCell: usageCellProps,
            render: record => helpers.numberWithCommas(record.total_bulk_sent)
        },
        {
            key: 'extra',
            title: 'Extra',
            width: isEmailUsageAnnual ? '25%' : '20%',
            onCell: usageCellProps,
            render: record => helpers.numberWithCommas(record.extra)
        },
        ...(!isEmailUsageAnnual
            ? [
                  {
                      key: 'cost',
                      title: 'Cost',
                      width: '20%',
                      onCell: usageCellProps,
                      render: record => '$' + helpers.numberWithCommas(record.extra_cost)
                  }
              ]
            : [])
    ];

    const smsColumns = [
        {
            key: 'month',
            title: 'Month',
            width: '20%',
            onCell: usageCellProps,
            render: record => moment(record.start_date).format('MMM YYYY')
        },
        {
            key: 'segments-sent',
            title: 'Segments Sent',
            width: '20%',
            onCell: usageCellProps,
            render: record => helpers.numberWithCommas(record.total_sent_segments)
        },
        {
            key: 'segments-received',
            title: 'Segments Received',
            width: '20%',
            onCell: usageCellProps,
            render: record => helpers.numberWithCommas(record.total_received_segments)
        },
        {
            key: 'total-segments',
            title: 'Total Segments',
            width: '20%',
            onCell: usageCellProps,
            render: record => helpers.numberWithCommas(record.total_segments)
        },
        {
            key: 'cost',
            title: 'Cost',
            width: '20%',
            onCell: usageCellProps,
            render: record =>
                accountSmsUsageUnlimited ? 'Included' : '$' + helpers.numberWithCommas(record.segments_cost)
        }
    ];

    return (
        <div className="flex">
            <AppPageWrapper>
                <Helmet>
                    <title>Usage</title>
                </Helmet>
                <div className="flex">
                    <SettingsPageSubNav />
                </div>
                <AppPageWrapperSectionHeader className="experience">
                    <div>
                        <H1>Usage</H1>
                        <div className="experience-description">Your usage reports.</div>
                    </div>
                </AppPageWrapperSectionHeader>
                <AppPageWrapperSectionBody>
                    <div className="mx-8 my-8 w-full">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-8">
                            {tremendousBalance && tremendousBalance.value !== null && (
                                <BalanceCard title="Tremendous Balance" icon={<FiDollarSign />} iconColor="blue">
                                    {helpers.renderCurrencyAmount(account.incentive_currency, tremendousBalance.value)}
                                </BalanceCard>
                            )}
                            {firstPartyBalance && firstPartyBalance.value !== null && (
                                <BalanceCard title="Incentive Balance" icon={<FiDollarSign />} iconColor="purple">
                                    {helpers.renderCurrencyAmount(account.incentive_currency, firstPartyBalance.value)}
                                </BalanceCard>
                            )}
                            {paidUsers !== null && (
                                <BalanceCard
                                    title="Users"
                                    icon={<FiUsers />}
                                    iconColor="green"
                                    footer={
                                        <p className="color-text-secondary fs-body-12 !m-0">
                                            Seats in use / Paid seats · Total users:{' '}
                                            {helpers.numberWithCommas(totalUsers?.value)}
                                        </p>
                                    }
                                >
                                    {allowedUsers > 0
                                        ? `${helpers.numberWithCommas(paidUsers.value)} / ${helpers.numberWithCommas(
                                              allowedUsers
                                          )}`
                                        : helpers.numberWithCommas(paidUsers.value)}
                                </BalanceCard>
                            )}
                            {totalPanels !== null && (
                                <BalanceCard title="Panels" icon={<FiGrid />} iconColor="yellow">
                                    {maxPanels > 0
                                        ? `${helpers.numberWithCommas(totalPanels.value)} / ${helpers.numberWithCommas(
                                              maxPanels
                                          )}`
                                        : helpers.numberWithCommas(totalPanels.value)}
                                </BalanceCard>
                            )}
                            {totalPanelists !== null && (
                                <BalanceCard
                                    title="Panelists"
                                    icon={<FiUser />}
                                    iconColor="purple"
                                    footer={
                                        totalPanelists.updated_at && (
                                            <p className="color-text-secondary fs-body-12 !m-0">
                                                Last calculated: {moment(totalPanelists.updated_at).fromNow()}
                                            </p>
                                        )
                                    }
                                >
                                    {maxPanelists > 0
                                        ? `${helpers.numberWithCommas(
                                              totalPanelists.value
                                          )} / ${helpers.numberWithCommas(maxPanelists)}`
                                        : helpers.numberWithCommas(totalPanelists.value)}
                                </BalanceCard>
                            )}
                        </div>
                        <H2 style={{ marginBottom: '16px' }}>Your email usage throughout the month.</H2>
                        {isLoading ? (
                            <div className="m-4 min-h-6">
                                <LoadingWrapper>
                                    <LoadingIndicator />
                                </LoadingWrapper>
                            </div>
                        ) : emailUsageByMonth.length > 0 ? (
                            <TableV2 className="border" columns={emailColumns} data={emailUsageByMonth} fixed />
                        ) : (
                            <div className="text-sm m-4">We don't have any usage recorded.</div>
                        )}

                        <H2 className="mt-8" style={{ marginBottom: '16px' }}>
                            Your SMS usage throughout the month.
                        </H2>
                        {isLoading ? (
                            <div className="m-4 min-h-6">
                                <LoadingWrapper>
                                    <LoadingIndicator />
                                </LoadingWrapper>
                            </div>
                        ) : smsUsageByMonth.length > 0 ? (
                            <TableV2 className="border" columns={smsColumns} data={smsUsageByMonth} fixed />
                        ) : (
                            <div className="text-sm m-4">We don't have any usage recorded.</div>
                        )}
                    </div>
                </AppPageWrapperSectionBody>
            </AppPageWrapper>
        </div>
    );
}

export default SettingsUsagePage;
